import { useRef, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';

/**
 * This hook monitors a container for overflowing children, returning the
 * visible and hidden items based on the available space.
 */
export const useOverflowedChildren = <T extends HTMLDivElement>(
  buttonWidths: Record<string, number>
) => {
  const containerRef = useRef<T>(null);

  const [itemData, setItemData] = useState<{
    items: string[];
    overflowedItems: string[];
  }>({
    items: Object.keys(buttonWidths),
    overflowedItems: [],
  });

  useEffect(
    () => {
      const container = containerRef.current;
      if (!container || Object.keys(buttonWidths).length === 0) return;

      const updateVisibility = debounce(() => {
        const containerWidth = container.clientWidth;
        let totalWidth = 0;

        const splitIndex = Object.keys(buttonWidths).findIndex((key) => {
          totalWidth += buttonWidths[key] ?? 0;
          return totalWidth > containerWidth;
        });

        const cutoffIndex =
          splitIndex === -1 ? Object.keys(buttonWidths).length : splitIndex;

        setItemData({
          items: Object.keys(buttonWidths).slice(0, cutoffIndex),
          overflowedItems: Object.keys(buttonWidths).slice(cutoffIndex),
        });
      }, 500);

      const observer = new ResizeObserver(updateVisibility);
      observer.observe(container);

      return () => {
        observer.disconnect();
        updateVisibility.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Avoid buttonWidths dep as it causes re-renders
    [JSON.stringify(buttonWidths), containerRef]
  );

  return { ...itemData, containerRef };
};
